/**
 * Consumer feed filter — the pure half of Stage C (consumer-feed-filters spec
 * @ `Danielle-design/consumer-feed-filters`, tip ≥ 5fffd8f5; wire truth =
 * hydraedge `Danielle-feature/consumer-feed-filters-edge` @ 6926e11b and
 * hydracore `Danielle-feature/consumer-feed-filters-core` @ 275ddd64, which
 * agree field-for-field on everything this file depends on).
 *
 * Shared by BOTH transports — the SharedWorker (`hydraWorker.ts`) and
 * HydraClient's direct-WS path — and by the tab-side stores, so the three
 * places that must agree (what we send, what we admit, what we purge) read one
 * definition. Nothing here touches a socket, a port, or React.
 *
 * Three pieces:
 *
 *  1. The versioned `filter` object and the row predicate (`rowPassesFilter`)
 *     — the edge/core `connWantsMarket` semantics verbatim: axes AND;
 *     `tickers`/`sports` are allow-lists (a row must CARRY a matching value);
 *     `excludeClasses`/`excludeMarketTypes` are deny-lists with the A1
 *     per-row fail-open rule (a row lacking the field always passes). The
 *     client evaluates the SAME predicate on every filterReset to decide which
 *     cached rows are now out of scope — so a row the server would no longer
 *     send is never left authoritative locally.
 *
 *  2. `FeedFilterAggregator` — per-source needs → live union → debounced
 *     diff, widening at once and the rest after a short flicker hold (route
 *     transactions, 2026-10-02), applied to the
 *     class/type/sports axes. Sources are OPT-INS: the connection starts from
 *     the browser defaults (props + futures excluded — ≈35–40% of rows, the
 *     torrent behind the 8/30 book freezes) and a view that renders props or
 *     futures declares the classes/types it needs, which REMOVES them from the
 *     exclusion set for as long as it is mounted (+ grace). Widening is
 *     immediate; narrowing waits out the grace so flipping between views that
 *     share a need does not churn replacement snapshots.
 *
 *  3. `FilterFence` — the A2′ client contract: install the ack'd
 *     (socketIncarnation, filterGeneration) pair, admit ONLY frames stamped
 *     with that exact pair, reset at every socket boundary. Stale-socket and
 *     stale-generation frames are dropped by construction.
 */
import { altBaseWireCode, altWireTokenFor, isAltWireToken } from '../lib/feedSport';
import { SPORT_NORMALIZE } from '../lib/sportRegistry';


// ── Wire shapes ─────────────────────────────────────────────────────────────

/** The versioned markets-channel filter object exactly as sent on the wire
 *  (`{op:'subscribe', channel:'markets', filter}`). Presence = the COMPLETE
 *  filter from now on (replace, never union). An axis is OMITTED when it
 *  constrains nothing — the server REJECTS explicit empty arrays. */
export interface MarketFilter {
  tickers?: string[];
  sports?: string[];
  /** `canonicalGameKey` values — ONE event as an allow-list (games axis;
   *  hydracore e37e33d, hydraedge c93b18e). Either-key membership: a row
   *  passes if EITHER its legacy key or its doubleheader-safe V2 key is
   *  listed. A row carrying NEITHER fails, per A1's allow-list rule. */
  games?: string[];
  excludeClasses?: string[];
  excludeMarketTypes?: string[];
  /** PROPOSED line-scope axis (g1xl-v2#1053 Part 3, path 1): `'main'` asks
   *  the engine for each game's drawn lines only — the moneyline pair plus
   *  the spread and total strikes nearest even money — instead of the whole
   *  alternate ladder (Poly lists 54 spreads per CFB game since 2026-10-01).
   *  NO engine serves this today; it rides ONLY when the connection's
   *  announce advertised it (`activeSports.filterAxes` ∋ `'lineScope'`,
   *  see `buildMarketFilter`), so a non-advertising engine never sees an
   *  unknown axis. Until an engine ships it, the board narrows by naming
   *  its rows on the `tickers` axis (path 2). */
  lineScope?: 'main';
  cadenceMs?: number;
}

/** `filterAck` — answered for every filter-bearing subscribe, BEFORE any frame
 *  of the new generation. `effective` echoes the applied filter plus the
 *  effective cadence; on rejection `error` is set and the prior generation is
 *  retained (never a silent clear). `socketIncarnation` is server-minted and
 *  globally unique across reconnects/restarts — equality-compare only. */
export interface HydraFilterAckMsg {
  type: 'filterAck';
  channel: 'markets';
  socketIncarnation: string;
  filterGeneration: number;
  effective?: MarketFilterEffective;
  error?: string;
}

/** `marketRemoved` — the games-axis release gate (hydra review DNL-003).
 *  Sent on FILTERED connections only, when an ALREADY-DELIVERED row's identity
 *  change makes it fail the installed filter: re-keyed onto another game, or
 *  stripped keyless. It is the one case a filtered feed cannot express by
 *  omission — the consumer already holds the row, and no future frame would
 *  ever contradict it.
 *
 *  Stamped with the same pair every market frame carries, so it goes through
 *  the SAME fence: a removal stamped with anything but the installed pair is
 *  stale by construction and dropped, exactly like a stale market frame.
 *
 *  Filter CHANGES deliver no removals — the replacement snapshot after each
 *  filterAck re-establishes scope, and `applyFilterReset` purges what the new
 *  scope excludes. This message is only for identity changes inside a
 *  generation. */
export interface HydraMarketRemovedMsg {
  type: 'marketRemoved';
  ticker: string;
  socketIncarnation?: string;
  filterGeneration?: number;
  serverTs: number;
  seq?: number;
}

export interface MarketFilterEffective {
  tickers?: string[];
  sports?: string[];
  games?: string[];
  excludeClasses?: string[];
  excludeMarketTypes?: string[];
  /** Echoed by an engine that applied the proposed line-scope axis. Never
   *  re-evaluated locally (`rowPassesFilter` ignores it): which strikes are
   *  "main" is the engine's predicate, and every row it sent under this
   *  scope is in scope by definition. */
  lineScope?: 'main';
  cadenceMs: number;
  /** max(requested, pressureCadence) on the edge; the core floor additionally
   *  composes its pinned 250 ms producer coalesce (a request of 25 acks 250).
   *  Read THIS, never the request. */
  effectiveCadenceMs: number;
}

/** The fencing pair stamped on every markets frame of a filtered connection. */
export interface FencePair {
  socketIncarnation: string;
  filterGeneration: number;
}

/** Worker → tab (shared mode) / client → store (direct mode): ONE atomic reset
 *  per installed generation, emitted BEFORE any post-fence frame. Receivers
 *  drop every cached row that fails `scope` (evaluated with rowPassesFilter);
 *  the replacement snapshot that follows repopulates. `scope` is the SERVER's
 *  effective echo — the applied filter, not the request. */
export interface FilterResetMsg extends FencePair {
  type: '__filterReset';
  scope: MarketFilterEffective;
}

// ── Defaults ────────────────────────────────────────────────────────────────

/** Browser-class cadence (spec R4 consumer class 1: 250+; never lower). */
export const BROWSER_CADENCE_MS = 250;
/** The tickers allow-list sent when every mounted source is ticker-scoped
 *  and none has a row (a tracker with no positions, alone in its browser).
 *  Matches no engine ticker by construction, so the connection carries
 *  nothing instead of everything. See `buildMarketFilter`. */
export const NO_ROWS_TICKER = '__NO_ROWS__';
/** The warm-standby tier (Dan, 2026-09-02): the core floor connection carries
 *  every sport at the contract's ceiling while an edge owns what is on
 *  screen; it drops back to BROWSER_CADENCE_MS whenever it is the ONLY
 *  source for a viewed sport (edge down / not announcing). 1000 is the
 *  clamp maximum on both engines — longer needs a spec amendment. */
export const FLOOR_STANDBY_CADENCE_MS = 1000;

/** What a game-centric view never renders. `player-prop` is the class core
 *  stamps on every player ladder (the finer, core-owned authority — A1's
 *  reconciliation note: Kalshi prop rows carry marketType 'prop' only where
 *  edge parsers stamp it, so class is the reliable axis).
 *
 *  `future` is deliberately NOT excluded (g1.pm smoke, 2026-09-02, Dan:
 *  "futures are flat out missing from the dashboard"). The dashboard's
 *  Futures tab (`MARKET_TYPE_TABS` 'series' → `getSeriesEvents`) renders the
 *  Kalshi championship outcomes (`KXNBA-26-LAL`, …), which core files under
 *  `group: futures` and the edge therefore stamps `marketType:'future'` —
 *  the exclusion blanked that tab. The spec's exclude-by-default candidate
 *  was the UNKEYED Poly season futures, and `marketType` cannot separate
 *  those from the Kalshi boards g1 renders (no venue axis); a future
 *  `excludeClasses` value core stamps only on unrenderable rows would.
 *  Phase 1 is therefore player-props only. */
export const DEFAULT_EXCLUDED_CLASSES: readonly string[] = [
  // Dan, 2026-09-02: the front page is the bet-group toggles — Moneyline,
  // Spread/Total, Futures. Everything only GameDetail (or a props surface)
  // renders is trimmed across the board; those surfaces opt it back in
  // while mounted, and the account plane while it holds such a ticker.
  // Kept on the wire: moneyline, spread, total, advance, series*, futures.
  // NOT filterable here: period lines (1H/F5/quarters) — `period` is its
  // own axis and the filter grammar has no field for it (spec follow-up).
  'player-prop',
  'team-total', 'team-stat',
  'half-fulltime', 'overtime', 'two-point', 'first-to-score', 'first-td-team',
  'highest-quarter', 'every-quarter-btts', 'total-touchdowns', 'total-fieldgoals',
  'btts', 'rfi', 'extras',
  // NFL game props (hydracore nfl-game-lines-props, on main 2026-09-03):
  // GameDetail-only families, same rule.
  'race-to', 'both-teams-points',
  // Seen on the wire since (main-alt-partition-he6-he7 D1, 2026-09-26): the
  // one class the ALT rule adds over the list above. This array IS the
  // published `ALT_CLASSES` (HYDRA_API.md § "ALT pseudo-sports") — the
  // lockstep test pins the two byte-for-byte; extend both or neither.
  'overtime-count',
];
export const DEFAULT_EXCLUDED_MARKET_TYPES: readonly string[] = [];
/** The classes a GAME DETAIL view renders beyond the front page — i.e. every
 *  default exclusion; it opts them all back in for its game's duration. */
export const GAME_DETAIL_CLASSES: readonly string[] = DEFAULT_EXCLUDED_CLASSES;

// ── Needs ───────────────────────────────────────────────────────────────────

/** What one source (a view, the account plane, a tab) needs from the feed.
 *  - `classes` / `marketTypes`: normally-excluded values this source renders —
 *    an OPT-IN that removes them from the exclusion set while declared.
 *  - `sports`: Phase 2 only. The sports this source renders (g1 `Sport` ids or
 *    hydra wire codes — normalized to uppercase wire codes here). `'all'` or
 *    ABSENT means the source needs every sport: a mounted source that does not
 *    say otherwise keeps the connection broad, so an undeclared view can never
 *    be starved by a sibling's narrower declaration. */
export interface FeedNeeds {
  /** Tickers axis (feed-filters-per-view, 2026-09-12). The EXACT rows this
   *  source renders — a tracker's held positions, a trader detail's book, a
   *  bot's configured pairs. A source that declares `tickers` is TICKER-
   *  SCOPED; one that does not is ticker-broad (it renders every row its
   *  other axes admit). The union puts the allow-list on the wire ONLY when
   *  every mounted source is ticker-scoped — an allow-list ANDs with every
   *  other axis, so beside a broad sibling (the dashboard in another tab of
   *  the same browser) it would starve that sibling. A ticker-scoped source
   *  therefore ALSO declares the fold of its rows (`sports`, `games`,
   *  `classes` — see `lib/feedFilter/tickerNeeds`): those are what carry
   *  its rows when the allow-list cannot ride. An explicit empty list is a
   *  ticker-scoped source with nothing to show (a tracker with no
   *  positions): when nothing else is mounted the wire says so with the
   *  `NO_ROWS_TICKER` sentinel instead of falling open to the firehose. */
  tickers?: Iterable<string>;
  classes?: Iterable<string>;
  marketTypes?: Iterable<string>;
  sports?: Iterable<string> | 'all';
  /** The sports a source names EVEN WHEN the union is broad — the publisher
   *  boundary's carrier for `AggregatedNeeds.namedSports` (D5.4). A tab whose
   *  union is `'all'` (a broad sibling view) but whose GameDetail names
   *  `nflalt` still has to unmute the ALT edge; `sports:'all'` alone would
   *  lose the name. Folded into `sport:<S>` keys beside `sport:*`; it never
   *  narrows anything (the union stays broad) — it only records the names. */
  namedSports?: Iterable<string>;
  /** Games axis. Same rule as `sports`, one level narrower: `'all'` or ABSENT
   *  means this source needs EVERY game, so a mounted source that does not say
   *  otherwise keeps the connection broad and can never be starved by a
   *  sibling's one-event declaration. A source scoped to no game of its own
   *  (the account plane — its held tickers are marked from the core floor,
   *  which never carries this axis) declares the empty list. */
  games?: Iterable<string> | 'all';
  /** Line-scope axis (g1xl-v2#1053 Part 3, path 1). `'main'`: this source
   *  draws only each game's main lines (the board). `'all'` or ABSENT: it
   *  draws the whole ladder (GameDetail, SpreadsTotals' expanded strip) —
   *  the broad default, so an undeclared view can never be starved. `'any'`:
   *  this source has no opinion (a ticker-scoped source — its rows are named
   *  outright; the account plane's off-page marks ride the core floor, which
   *  never carries this axis). The axis rides the wire only when every
   *  mounted source is `'main'` or `'any'`, AND the engine advertised it. */
  lineScope?: 'main' | 'all' | 'any';
  /** Markets cadence this source wants (g1xl-v2#1053 Part 2d). ABSENT = the
   *  browser default (250 ms — nothing slows down unless it asks); a number
   *  = that; `null` = no opinion (a ticker-scoped floor source: its off-page
   *  marks ride the core floor at its own cadence). The union takes the
   *  FASTEST opinion; the connection sends `max(policy, union)` so a core's
   *  standby policy still floors it. The board declares 1000 unless a game
   *  is focused (a row expanded, the ticket open) — then 250. */
  cadenceMs?: number | null;
  /** Sports this source needs carried on the CORE FLOOR even though it
   *  declares no `sports` of its own (g1xl-v2#1053 Part 2e): the account
   *  plane's held rows. The floor connection's sports axis is the viewed
   *  union ∪ these — so hc1 stops carrying every sport at 1 s for a tab
   *  that looks at one. Never widens an edge. */
  floorSports?: Iterable<string>;
  /** This declaration is a stop on the way to a narrower one (a board whose
   *  allow-list is still being fetched declares its empty list provisional):
   *  the transports hold the previous scope on the wire instead of emitting
   *  the intermediate, bounded by `PROVISIONAL_MAX_MS`. */
  provisional?: boolean;
  /** Per-sport attribution of the class / market-type opt-ins (per-edge
   *  scope, 2026-10-02): the publisher boundary carries the tab's union to
   *  the worker in this shape so a GameDetail's `player-prop` opt-in for
   *  NFL never reaches the CFB edge. `'*'` = opted in for every sport. A
   *  source that declares `classes` beside an explicit `sports` list is
   *  attributed to those sports automatically; this field is for re-publishing
   *  an already-aggregated union. */
  classesBySport?: Record<string, Iterable<string>>;
  marketTypesBySport?: Record<string, Iterable<string>>;
  /** Per-sport attribution of a BROAD tickers / games axis (per-edge scope):
   *  which sport keys this source is ticker-broad / game-broad FOR (`'*'` =
   *  every sport). A source that omits `tickers` beside an explicit `sports`
   *  list is attributed to those sports automatically; these fields are for
   *  re-publishing an already-aggregated union. */
  tickersBroadFor?: Iterable<string>;
  gamesBroadFor?: Iterable<string>;
  /** Every ticker / game key some source NAMED, re-published beside a broad
   *  axis (the tab's union is ticker-broad for CFB but its NFL tracker
   *  named its rows): the worker builds the NFL edge's allow-list from
   *  these. For re-publishing an aggregated union only. */
  namedTickers?: Iterable<string>;
  namedGames?: Iterable<string>;
  /** ALT events (wedge issue 1, 2026-10-02 — #1052's idea on #1053's
   *  code): the games a source named BESIDE an ALT key (`<SPORT>ALT`). Kept
   *  apart from `games` because a sibling that is broad for EVERY sport (the
   *  All-sports board, Live, an unaudited route — `sports:'all'`) erases the
   *  union's games axis for the ALT edge too, and an ALT edge must never get
   *  its whole sport (he6 took all of CFBALT on gbuff 2026-10-02). A
   *  single-sport board does not: per-edge scope attributes its breadth to
   *  its own sport, not the ALT key. Leaf sources leave
   *  it unset — it is derived from their ALT name + `games`; the publisher
   *  boundary carries the union's set across. */
  altGames?: Iterable<string>;
  /** OddsPapi facets (odds-gamestate-feed-filters D6) — what this source
   *  draws from the `oddspapi` channel. ABSENT = no OP (see OddsNeeds). The
   *  markets axes above are independent: a view that draws both declares
   *  both; one that draws only OP uses `oddsOnlyNeeds`. */
  odds?: OddsNeeds;
}

/** The union across sources — the input to `buildMarketFilter`. */
export interface AggregatedNeeds {
  /** Exact tickers, or null = the union is ticker-broad (some source renders
   *  rows it cannot name). An EMPTY set is "nothing mounted names a row"
   *  (every source ticker-scoped and empty, or no source at all) — muted on
   *  the wire, never broad. */
  tickers: Set<string> | null;
  /** Classes that must NOT be excluded. */
  classes: Set<string>;
  /** Market types that must NOT be excluded. */
  marketTypes: Set<string>;
  /** Uppercase wire sport codes, or null = every sport (broad). */
  sports: Set<string> | null;
  /** Every sport any source NAMED, uppercase, whether or not the union is
   *  broad (`sports` null) — including names still inside the narrowing
   *  hold. The ALT-only mute (D5.4) reads THIS: a GameDetail that names
   *  `NFLALT` unmutes he7 even while a dashboard sibling keeps the sports
   *  axis broad.
   *  Never on the wire; derived from the keys. Optional so hand-built
   *  fixtures (tests) stay valid; absent reads as "nothing named". */
  namedSports?: Set<string>;
  /** canonicalGameKey values, or null = every game (broad). */
  games: Set<string> | null;
  /** `'main'` = every mounted source is line-scoped (or indifferent) and at
   *  least one asked for main lines; `'all'` = some source draws the whole
   *  ladder; `'any'` = nobody said anything (no source, or ticker-scoped
   *  sources only). Optional so hand-built fixtures stay valid; absent reads
   *  as `'any'`. */
  lineScope?: 'main' | 'all' | 'any';
  /** The fastest cadence any source asked for, or null = nobody has an
   *  opinion (the connection's policy cadence applies). Optional so
   *  hand-built fixtures stay valid. */
  cadenceMs?: number | null;
  /** Uppercase wire sport codes the core floor must carry beside the viewed
   *  union (Part 2e). Optional so hand-built fixtures stay valid. */
  floorSports?: Set<string>;
  /** Any source is provisional (see FeedNeeds.provisional). Never on the
   *  wire; carried tab → worker so the worker's hold sees it. */
  provisional?: boolean;
  /** Class / market-type opt-ins by sport key (`'*'` = every sport). The
   *  flat `classes` / `marketTypes` stay the global union (what a core floor
   *  or an unscoped connection opts in); `scopeMarketFilter` rebuilds the
   *  deny-lists per edge from these. Optional so fixtures stay valid. */
  classesBySport?: Map<string, Set<string>>;
  marketTypesBySport?: Map<string, Set<string>>;
  /** Sport keys some source is ticker-broad / game-broad FOR (`'*'` = every
   *  sport); every named ticker / game key, even when the global axis is
   *  broad. `scopeMarketFilter` builds an edge's allow-lists from these:
   *  an NFL tracker's rows ride to he5 while a CFB board keeps he2 broad.
   *  Optional so fixtures stay valid. */
  tickersBroadFor?: Set<string>;
  gamesBroadFor?: Set<string>;
  namedTickers?: Set<string>;
  namedGames?: Set<string>;
  /** The ALT events of the union (see FeedNeeds.altGames). */
  altGames?: Set<string>;
  /** The OddsPapi facets of the union (see FeedNeeds.odds). Optional so
   *  hand-built fixtures stay valid; absent reads as "no OP drawn". */
  odds?: AggregatedOdds;
}

/** What one source draws from the `oddspapi` channel (odds-gamestate-feed-
 *  filters D6). Unlike the markets axes, an ABSENT facet means "draws no
 *  OP": the OP filter is an allow-list (spec D2), so a source that says
 *  nothing adds nothing — every OP reader declares what it draws. */
export interface OddsNeeds {
  /** BOARD facet: OP for every game of `sports` (`'all'` = every sport) in
   *  these families (`OpMarket.marketType`, lower-cased) and periods
   *  (`'full'` = the entries carrying periodTypeId 1; any other value is a
   *  vendor `period` verbatim). A facet with no sport, family or period
   *  draws nothing and is dropped. */
  board?: { sports: Iterable<string> | 'all'; marketTypes: Iterable<string>; periods: Iterable<string> };
  /** DETAIL facet: games this source draws WHOLE — every family, every
   *  period. Exact OP game keys (legacy or V2). */
  detailGames?: Iterable<string>;
}

/** The union of every source's OddsPapi facets. The board facets union as a
 *  cross product (an NFL moneyline board beside a CFB spread/total board
 *  draws moneyline, spread and total for both sports): a superset, never a
 *  starve (spec D6). */
export interface AggregatedOdds {
  /** Some board facet is open. */
  board: boolean;
  /** The board facets' sports, uppercase; null = some board draws every sport. */
  boardSports: Set<string> | null;
  marketTypes: Set<string>;
  periods: Set<string>;
  detailGames: Set<string>;
}

export function emptyOdds(): AggregatedOdds {
  return { board: false, boardSports: new Set(), marketTypes: new Set(), periods: new Set(), detailGames: new Set() };
}

export function emptyNeeds(): AggregatedNeeds {
  return { tickers: null, classes: new Set(), marketTypes: new Set(), sports: null, games: null, namedSports: new Set(), lineScope: 'any', cadenceMs: null, floorSports: new Set(), classesBySport: new Map(), marketTypesBySport: new Map(), tickersBroadFor: new Set(), gamesBroadFor: new Set(), namedTickers: new Set(), namedGames: new Set(), altGames: new Set(), odds: emptyOdds() };
}

/** A source that declares ONLY OddsPapi facets (the trade ticket): every
 *  markets axis is "nothing of my own" — no ticker, sport, game, line or
 *  cadence opinion — so it never widens the markets filter. A plain
 *  `{ odds }` would read as markets-BROAD (absent = every sport, every game). */
export function oddsOnlyNeeds(odds: OddsNeeds): FeedNeeds {
  return { tickers: [], sports: [], games: [], lineScope: 'any', cadenceMs: null, odds };
}

const normSport = (s: string): string => s.trim().toUpperCase();
const normLower = (s: string): string => s.trim().toLowerCase();

/** Reduce one source's declaration to the aggregator's key set. Keys are
 *  namespaced strings so one grace map covers every axis:
 *  `class:<c>`, `type:<t>`, `sport:<S>`, and `sport:*` for "every sport". */
export function needsToKeys(needs: FeedNeeds): Set<string> {
  const keys = new Set<string>();
  // Opt-ins and BROAD markers are attributed to the source's sports (per-edge
  // scope, 2026-10-02): a GameDetail's classes, and its "every game / every
  // ticker" of ITS sport, must not widen an edge that serves another sport.
  // A source with no sport list (or 'all') is attributed to every sport.
  const scopedTo = needs.sports !== undefined && needs.sports !== 'all'
    ? [...needs.sports, ...(needs.namedSports ?? [])].map(normSport).filter(Boolean)
    : [];
  const broadKeys = (kind: 'ticker' | 'game', attributed: Iterable<string> | undefined): void => {
    const sports = attributed ? [...attributed].map((s) => (s === '*' ? '*' : normSport(s))).filter(Boolean) : scopedTo;
    if (sports.length === 0 || sports.includes('*')) keys.add(`${kind}:*`);
    else for (const s of sports) keys.add(`${kind}:*@${s}`);
  };
  if (needs.tickers === undefined) {
    broadKeys('ticker', needs.tickersBroadFor);
  } else {
    for (const t of needs.tickers) { const v = typeof t === 'string' ? t.trim() : ''; if (v) keys.add(`ticker:${v}`); }
  }
  for (const t of needs.namedTickers ?? []) { const v = typeof t === 'string' ? t.trim() : ''; if (v) keys.add(`ticker:${v}`); }
  for (const g of needs.namedGames ?? []) { const v = typeof g === 'string' ? g.trim() : ''; if (v) keys.add(`game:${v}`); }
  const optIn = (kind: 'class' | 'type', raw: string, sports: readonly string[]): void => {
    const v = normLower(raw);
    if (!v) return;
    if (sports.length === 0) keys.add(`${kind}:${v}`);
    else for (const s of sports) keys.add(`${kind}:${v}@${s}`);
  };
  if (needs.classesBySport) {
    for (const [sport, list] of Object.entries(needs.classesBySport)) for (const c of list) optIn('class', c, sport === '*' ? [] : [normSport(sport)]);
  } else {
    for (const c of needs.classes ?? []) optIn('class', c, scopedTo);
  }
  if (needs.marketTypesBySport) {
    for (const [sport, list] of Object.entries(needs.marketTypesBySport)) for (const t of list) optIn('type', t, sport === '*' ? [] : [normSport(sport)]);
  } else {
    for (const t of needs.marketTypes ?? []) optIn('type', t, scopedTo);
  }
  if (needs.games === undefined || needs.games === 'all') {
    broadKeys('game', needs.gamesBroadFor);
  } else {
    // Keys are opaque (they contain '|', never ':'), so no normalization —
    // the engines compare them by exact string equality.
    for (const g of needs.games) { const v = typeof g === 'string' ? g.trim() : ''; if (v) keys.add(`game:${v}`); }
  }
  if (needs.sports === undefined || needs.sports === 'all') {
    keys.add('sport:*');
  } else {
    // An explicit (possibly empty) list adds exactly those sports — an empty
    // list is a source that needs no sport of its own (the account plane
    // with no positions), never "every sport".
    for (const s of needs.sports) { const v = normSport(s); if (v) keys.add(`sport:${v}`); }
  }
  // Names beside a broad union (see FeedNeeds.namedSports): same keys, so
  // the grace and the union treat them like any declared sport.
  for (const s of needs.namedSports ?? []) { const v = normSport(s); if (v) keys.add(`sport:${v}`); }
  // Line scope: absent = the whole ladder (broad, like every other axis);
  // 'any' contributes no key at all.
  if (needs.lineScope === 'main') keys.add('lines:main');
  else if (needs.lineScope !== 'any') keys.add('lines:*');
  // Cadence (Part 2d): absent = the browser default is this source's
  // opinion; null = none. A slower cadence is held like any narrowing, so a
  // focus that lifts keeps the faster cadence through the flicker hold.
  if (needs.cadenceMs !== null) {
    const c = needs.cadenceMs ?? BROWSER_CADENCE_MS;
    if (Number.isFinite(c) && c > 0) keys.add(`cadence:${Math.round(c)}`);
  }
  for (const s of needs.floorSports ?? []) { const v = normSport(s); if (v) keys.add(`floor:${v}`); }
  // ALT events (see FeedNeeds.altGames): a source naming an ALT key beside
  // explicit games declares those games for the ALT edge, under their own
  // keys so no broad sibling can erase them. A re-published union carries
  // its set verbatim instead.
  const declaredSports = needs.sports !== undefined && needs.sports !== 'all' ? [...needs.sports] : [];
  const namesAlt = [...declaredSports, ...(needs.namedSports ?? [])].some((s) => isAltWireToken(normSport(s)));
  const altGames = needs.altGames ?? (namesAlt && needs.games !== undefined && needs.games !== 'all' ? needs.games : []);
  for (const g of altGames) { const v = typeof g === 'string' ? g.trim() : ''; if (v) keys.add(`alt-game:${v}`); }
  // OddsPapi facets (D6), under their own `op-` keys so no markets axis can
  // read them. A board facet missing a sport, family or period draws
  // nothing and adds no key — so no OP axis is ever empty on the wire.
  const board = needs.odds?.board;
  if (board) {
    const types = [...board.marketTypes].map(normLower).filter(Boolean);
    const periods = [...board.periods].map(normPeriod).filter(Boolean);
    const sports = board.sports === 'all' ? ['*'] : [...board.sports].map(normSport).filter(Boolean);
    if (types.length > 0 && periods.length > 0 && sports.length > 0) {
      for (const s of sports) keys.add(`op-sport:${s}`);
      for (const t of types) keys.add(`op-type:${t}`);
      for (const p of periods) keys.add(`op-period:${p}`);
    }
  }
  for (const g of needs.odds?.detailGames ?? []) { const v = typeof g === 'string' ? g.trim() : ''; if (v) keys.add(`op-detail:${v}`); }
  return keys;
}

/** `'full'` is the one token (case-folded); a vendor period is verbatim. */
const normPeriod = (p: string): string => {
  const v = typeof p === 'string' ? p.trim() : '';
  return v.toLowerCase() === 'full' ? 'full' : v;
};

export function keysToNeeds(keys: Iterable<string>): AggregatedNeeds {
  const out = emptyNeeds();
  let allSports = false;
  let allGames = false;
  let allTickers = false;
  let allLines = false;
  let mainLines = false;
  const sports = new Set<string>();
  const games = new Set<string>();
  const tickers = new Set<string>();
  const floor = new Set<string>();
  let cadence: number | null = null;
  const classesBySport = new Map<string, Set<string>>();
  const typesBySport = new Map<string, Set<string>>();
  const attribute = (map: Map<string, Set<string>>, body: string): string => {
    const at = body.lastIndexOf('@');
    const sport = at >= 0 ? body.slice(at + 1) : '*';
    const value = at >= 0 ? body.slice(0, at) : body;
    let set = map.get(sport);
    if (!set) { set = new Set(); map.set(sport, set); }
    set.add(value);
    return value;
  };
  const tickersBroadFor = new Set<string>();
  const gamesBroadFor = new Set<string>();
  const altGames = new Set<string>();
  const odds = emptyOdds();
  let opAllSports = false;
  for (const k of keys) {
    // OddsPapi facets first: `op-` keys never match a markets branch below,
    // but keeping them apart makes that unconstructible.
    if (k.startsWith('op-')) {
      if (k === 'op-sport:*') { odds.board = true; opAllSports = true; }
      else if (k.startsWith('op-sport:')) { odds.board = true; odds.boardSports!.add(k.slice(9)); }
      else if (k.startsWith('op-type:')) odds.marketTypes.add(k.slice(8));
      else if (k.startsWith('op-period:')) odds.periods.add(k.slice(10));
      else if (k.startsWith('op-detail:')) odds.detailGames.add(k.slice(10));
      continue;
    }
    if (k === 'ticker:*') { allTickers = true; tickersBroadFor.add('*'); }
    else if (k.startsWith('ticker:*@')) { allTickers = true; tickersBroadFor.add(k.slice(9)); }
    else if (k.startsWith('ticker:')) tickers.add(k.slice(7));
    else if (k === 'game:*') { allGames = true; gamesBroadFor.add('*'); }
    else if (k.startsWith('game:*@')) { allGames = true; gamesBroadFor.add(k.slice(7)); }
    else if (k === 'lines:*') allLines = true;
    else if (k === 'lines:main') mainLines = true;
    else if (k.startsWith('cadence:')) { const c = Number(k.slice(8)); if (Number.isFinite(c) && (cadence == null || c < cadence)) cadence = c; }
    else if (k.startsWith('floor:')) floor.add(k.slice(6));
    else if (k.startsWith('alt-game:')) altGames.add(k.slice(9));
    else if (k.startsWith('class:')) out.classes.add(attribute(classesBySport, k.slice(6)));
    else if (k.startsWith('type:')) out.marketTypes.add(attribute(typesBySport, k.slice(5)));
    else if (k === 'sport:*') allSports = true;
    else if (k.startsWith('sport:')) sports.add(k.slice(6));
    else if (k.startsWith('game:')) games.add(k.slice(5));
  }
  out.sports = allSports ? null : sports;
  out.namedSports = sports;
  out.games = allGames ? null : games;
  out.altGames = altGames;
  // Ticker-broad the moment ANY source is (it renders rows it cannot name);
  // otherwise the exact union — which for an EMPTY key set is the empty set:
  // a tab with nothing mounted names no rows, and `buildMarketFilter` says so
  // on the wire instead of pulling the firehose (the floor still carries
  // every sport at its standby cadence, so nothing can go dark).
  out.tickers = allTickers ? null : tickers;
  out.lineScope = allLines ? 'all' : mainLines ? 'main' : 'any';
  out.cadenceMs = cadence;
  out.floorSports = floor;
  out.classesBySport = classesBySport;
  out.marketTypesBySport = typesBySport;
  out.tickersBroadFor = tickersBroadFor;
  out.gamesBroadFor = gamesBroadFor;
  out.namedTickers = new Set(tickers);
  out.namedGames = new Set(games);
  if (opAllSports) odds.boardSports = null;
  // A board key set always carries all three axes (needsToKeys adds none of
  // them otherwise); a union missing one is not a board.
  if (odds.marketTypes.size === 0 || odds.periods.size === 0) odds.board = false;
  out.odds = odds;
  return out;
}

/** Build the wire filter from aggregated needs.
 *
 *  - Exclusions = defaults minus what any source needs. An emptied axis is
 *    OMITTED (explicit `[]` is rejected by the server).
 *  - `sports` rides only when the Phase-2 axis is on: the viewed sports —
 *    the UNION. Per-connection narrowing to what the engine serves is a
 *    second step, `scopeMarketFilter` (an empty intersection is the
 *    matches-nothing allow-list, never broad: the first cut fell back to
 *    BROAD on an empty intersection and the g1.pm spike showed he1 — tennis
 *    — streaming 330 KB/s on the NFL tab). `feedSports` is kept for callers
 *    but does not restrict here.
 *  - `cadenceMs` is always present: browsers negotiate 250 explicitly. */
export function buildMarketFilter(
  needs: AggregatedNeeds,
  opts: {
    cadenceMs?: number; sportsAxis?: boolean; gamesAxis?: boolean; tickersAxis?: boolean; feedSports?: ReadonlySet<string> | null;
    /** Axes THIS connection's engine advertised in its announce
     *  (`activeSports.filterAxes`). Only the proposed `lineScope` axis is
     *  gated on it; the shipped axes are governed by the mode flags above.
     *  null / absent = nothing advertised. */
    engineAxes?: ReadonlySet<string> | null;
    /** CORE FLOOR mode (g1xl-v2#1053 Part 2e): the sports axis is the viewed
     *  union ∪ `floorSports` (the held rows' sports) when the union is
     *  narrow; omitted (every sport, the floor of before) when it is broad,
     *  unless an ALT key is a cold standby (#1106), when it is the explicit
     *  `FLOOR_WIRE_SPORTS` list less that key.
     *  Replaces `sportsAxis` on core feeds — never set both. */
    floorAxis?: boolean;
    /** CORE FLOOR only (g1xl-v2#1106): the ALT tokens an edge is serving
     *  (uppercase wire codes, the fleet's `__filter_policy`). The floor axis
     *  drops each one the ALT edge is carrying for these needs — see
     *  `floorColdStandbySports`. Ignored outside `floorAxis`. */
    floorOmitSports?: Iterable<string> | null;
  } = {},
): MarketFilter {
  const filter: MarketFilter = {};
  // Tickers axis: when every mounted source names its rows, the allow-list
  // IS the filter. Nothing else rides with it — an allow-list of exact rows
  // makes the sport/game axes redundant, and a deny-list beside it could only
  // drop a row the source asked for by name (a held player-prop position).
  // Engines reject an explicit `[]`, and omitting the axis would mean the
  // firehose, so "these sources have no rows" is said with a sentinel that
  // matches nothing: the connection is narrow, acked, and quiet.
  // Cadence (Part 2d): the connection's policy (edge 250; core 1000 standby /
  // 250 sole source) floors the sources' fastest opinion — a board asking
  // 1000 slows an edge, a focused game or a GameDetail tab asking 250 does
  // not override a core's standby, and no opinion is the policy as before.
  const cadenceMs = Math.max(opts.cadenceMs ?? BROWSER_CADENCE_MS, needs.cadenceMs ?? BROWSER_CADENCE_MS);
  if (opts.tickersAxis && needs.tickers != null) {
    filter.tickers = needs.tickers.size > 0 ? [...needs.tickers].sort() : [NO_ROWS_TICKER];
    filter.cadenceMs = cadenceMs;
    return filter;
  }
  const excludeClasses = DEFAULT_EXCLUDED_CLASSES.filter((c) => !needs.classes.has(c));
  if (excludeClasses.length > 0) filter.excludeClasses = excludeClasses;
  const excludeMarketTypes = DEFAULT_EXCLUDED_MARKET_TYPES.filter((t) => !needs.marketTypes.has(t));
  if (excludeMarketTypes.length > 0) filter.excludeMarketTypes = excludeMarketTypes;
  if (opts.sportsAxis && needs.sports != null) {
    const wanted = [...needs.sports];
    if (wanted.length > 0) filter.sports = wanted.sort();
  } else if (opts.floorAxis) {
    // #1106: an ALT key on the floor is a COLD standby while an edge carries
    // it. Held rows' sports (`floorSports`) are never omitted.
    const omit = floorColdStandbySports(needs, opts.floorOmitSports);
    const held = needs.floorSports ?? new Set<string>();
    if (needs.sports != null) {
      let wanted = new Set([...needs.sports, ...held]);
      // An omission that would empty the list keeps it (an absent axis is
      // every sport, the opposite of what the omission is for).
      if (omit.size > 0) {
        const kept = new Set([...wanted].filter((s) => !omit.has(s) || held.has(s)));
        if (kept.size > 0) wanted = kept;
      }
      if (wanted.size > 0) filter.sports = [...wanted].sort();
    } else if (omit.size > 0) {
      // A broad union (Live, the All-sports board) beside the omission: the
      // engines have no "every sport except" axis, so the floor sends the
      // explicit list of every wire code g1 renders minus every spelling of
      // the omitted key (Dan, 2026-10-08). hydracore's `sports[]` allow-list
      // matches the row's routing key exactly, so the ALT section stays off
      // the floor. Nothing the tab could use is lost: a core frame is
      // admitted only for a sport g1 knows (feedShouldIngest). With no
      // omission the axis stays omitted, the floor of before.
      const omitted = new Set([...omit].map((t) => SPORT_NORMALIZE[altBaseWireCode(t) ?? ''] ?? t));
      const kept = FLOOR_WIRE_SPORTS.filter((code) => {
        if (held.has(code) || !isAltWireToken(code)) return true;
        return !omitted.has(SPORT_NORMALIZE[altBaseWireCode(code) ?? ''] ?? code);
      });
      filter.sports = [...new Set([...kept, ...held])].sort();
    }
  }
  if (opts.gamesAxis && needs.games != null) {
    const wanted = [...needs.games];
    if (wanted.length > 0) filter.games = wanted.sort();
  }
  // Proposed line-scope axis (g1xl-v2#1053 Part 3, path 1): on the wire only
  // when the engine said it understands it. A non-advertising engine gets
  // exactly the filter it got before this axis existed.
  if (needs.lineScope === 'main' && opts.engineAxes?.has('lineScope')) filter.lineScope = 'main';
  filter.cadenceMs = cadenceMs;
  return filter;
}

// ── Per-edge scope ──────────────────────────────────────────────────────────
// (g1 task "stop re-snapshotting every edge on every page change", 2026-10-02)
//
// The SharedWorker used to send the UNION of every tab's sports to EVERY
// edge: he2 received `[CFB, MLB]` though it serves only CFB, and every
// change to the union — a sport toggle, a GameDetail naming CFBALT, the
// drop afterwards — reached every edge as a new filter generation, each a
// full replacement snapshot (he2: 14,850 CFB rows / 18 MB). Each edge now
// gets the union ∩ what IT serves (its announce, else its claim), so a
// change that does not touch an edge's sports leaves its filter — and its
// generation — exactly where it was.

/** The sport family a feed key belongs to: `CFBALT` → `CFB`, else the key
 *  itself, uppercase. An ALT edge serves the ALT rows of its base family. */
export function sportFamily(code: string): string {
  const upper = code.trim().toUpperCase();
  return isAltWireToken(upper) ? (altBaseWireCode(upper) ?? upper) : upper;
}

/** The sport family a ticker belongs to, from its venue grammar alone —
 *  worker-safe (no parser, no registry). null = unknown: the ticker is sent
 *  to every edge (fail-open — an edge ignores a ticker it does not have). */
export function tickerSportFamily(ticker: string): string | null {
  const t = ticker.trim().toUpperCase();
  // Polymarket US: `PMUS-<market type>-<sport>-…` — `aec`/`asc`/`tsc`/
  // `astatc`/`aachc`/… then `cfb`/`nfl`/`nba` (every hepu-* edge, probed
  // 2026-10-13). Unclassified, a GameDetail's US rows reached EVERY edge
  // (fail-open) and re-filtered all of them on each page change.
  if (t.startsWith('PMUS-')) {
    const seg = t.split('-');
    return POLY_SPORT_FAMILY[seg[2]] ?? POLY_SPORT_FAMILY[seg[1]] ?? null;
  }
  if (t.startsWith('PM-')) {
    const seg = t.slice(3).split('-')[0];
    return POLY_SPORT_FAMILY[seg] ?? null;
  }
  for (const [prefix, family] of KALSHI_SPORT_FAMILY) if (t.startsWith(prefix)) return family;
  return null;
}
// Longest / most specific prefixes first — `KXNCAAF` (CFB) before `KXNCAA`
// (CBB), `KXWNBA` before `KXNBA`; the relay's broad-root map
// (relay/src/services/sports.ts) is the reference vocabulary.
const KALSHI_SPORT_FAMILY: ReadonlyArray<readonly [string, string]> = [
  ['KXNCAAF', 'CFB'], ['KXNCAA', 'CBB'], ['KXWNBA', 'WNBA'], ['KXNBA', 'NBA'], ['KXNFL', 'NFL'], ['KXSB', 'NFL'],
  ['KXNHL', 'NHL'], ['KXMLB', 'MLB'], ['KXATP', 'ATP'], ['KXWTA', 'WTA'], ['KXITFW', 'WTA'], ['KXITF', 'ATP'],
  ['KXUFC', 'UFC'], ['KXBOXING', 'BOXING'], ['KXMENWORLDCUP', 'SOCCER'], ['KXWOMENWORLDCUP', 'SOCCER'], ['KXWC', 'SOCCER'],
  ['KXUECL', 'SOCCER'], ['KXINTLFRIENDLY', 'SOCCER'], ['KXPGA', 'GOLF'], ['KXLPGA', 'GOLF'], ['KXLIV', 'GOLF'],
  ['KXDPWORLDTOUR', 'GOLF'], ['KXDPWT', 'GOLF'], ['KXRYDERCUP', 'GOLF'], ['KXCRICKET', 'CRICKET'],
];
const POLY_SPORT_FAMILY: Record<string, string> = {
  CFB: 'CFB', NCAAF: 'CFB', NFL: 'NFL', MLB: 'MLB', NBA: 'NBA', WNBA: 'WNBA', NHL: 'NHL', CBB: 'CBB', NCAAB: 'CBB',
  ATP: 'ATP', WTA: 'WTA', ITF: 'ATP', UFC: 'UFC', BOXING: 'BOXING', CRICKET: 'CRICKET', GOLF: 'GOLF', PGA: 'GOLF',
};

/** The sport family a canonical game key belongs to (`ncaaf|…` → `CFB`,
 *  `nfl|…` → `NFL`, `tennis|…` → `TENNIS`, which an ATP or WTA engine
 *  serves — see `familyServedBy`); null when the key has no sport segment. */
export function gameKeySportFamily(key: string): string | null {
  const seg = key.split('|')[0]?.trim().toUpperCase() ?? '';
  if (!seg) return null;
  return GAME_KEY_SPORT_FAMILY[seg] ?? seg;
}
const GAME_KEY_SPORT_FAMILY: Record<string, string> = { NCAAF: 'CFB', NCAAB: 'CBB', ITF: 'ATP' };

/** Families that are a GROUP of engine keys: a `tennis|…` game key (hydra
 *  stamps one key for both tours) belongs to an engine serving ATP or WTA.
 *  Verified on he1 2026-10-02: Kalshi `KXATPMATCH-…` and Poly `PM-wta-…`
 *  rows both carry `canonicalGameKey: "tennis|…"`. */
const FAMILY_GROUPS: Record<string, readonly string[]> = { TENNIS: ['ATP', 'WTA', 'TENNIS'] };

/** Does an engine serving `families` serve rows of `family`? null (unknown)
 *  is served everywhere (fail-open). */
export function familyServedBy(family: string | null, families: ReadonlySet<string>): boolean {
  if (family == null || families.has(family)) return true;
  const group = FAMILY_GROUPS[family];
  return group != null && group.some((f) => families.has(f));
}

/** Narrow a connection's filter to what ITS engine serves. `scope` = the
 *  uppercase keys the engine announced (else its claim); null / empty =
 *  no information yet → unchanged (today's union). Exact keys on the
 *  sports axis (`CFBALT` rides only to an edge announcing `CFBALT`); the
 *  family on the games and tickers axes (an ALT edge serves its family's
 *  game keys; an unknown ticker is kept — fail-open). An intersection with
 *  nothing left is the matches-nothing allow-list (narrow, acked, quiet),
 *  NEVER the broad filter: the first cut fell back to broad on an empty
 *  intersection and he1 (tennis) streamed 330 KB/s on the NFL tab. */
export function scopeMarketFilter(
  filter: MarketFilter,
  scope: ReadonlySet<string> | null | undefined,
  needs?: AggregatedNeeds,
  axes: { tickersAxis?: boolean; gamesAxis?: boolean } = {},
): MarketFilter {
  if (scope == null || scope.size === 0) return filter;
  const keys = new Set([...scope].map((k) => k.trim().toUpperCase()).filter(Boolean));
  const families = new Set([...keys].map(sportFamily));
  const cadenceMs = filter.cadenceMs ?? BROWSER_CADENCE_MS;
  const inFamily = (family: string | null): boolean => familyServedBy(family, families);
  const broadHere = (attributed: ReadonlySet<string> | undefined): boolean =>
    attributed == null || attributed.has('*') || [...keys].some((k) => attributed.has(k));
  // Tickers: an edge no source is ticker-broad FOR gets the named rows of
  // its families as its whole filter (an NFL tracker's rows ride to he5
  // while a CFB board keeps he2 ticker-broad) — the matches-nothing list
  // when it has none.
  if (axes.tickersAxis && needs?.tickersBroadFor && !broadHere(needs.tickersBroadFor)) {
    const kept = [...(needs.namedTickers ?? [])].filter((t) => inFamily(tickerSportFamily(t))).sort();
    return kept.length > 0 ? { tickers: kept, cadenceMs } : { tickers: [NO_ROWS_TICKER], cadenceMs };
  }
  // Opt-ins attributed to this engine's keys (plus the global ones) decide
  // ITS deny-lists: a GameDetail's NFL opt-ins never change he2's filter.
  const optedIn = (map: ReadonlyMap<string, ReadonlySet<string>> | undefined): Set<string> | null => {
    if (map == null) return null;
    const out = new Set<string>();
    for (const [sport, values] of map) if (sport === '*' || keys.has(sport)) for (const v of values) out.add(v);
    return out;
  };
  const scopedClasses = optedIn(needs?.classesBySport);
  const scopedTypes = optedIn(needs?.marketTypesBySport);
  const withDenyLists = (f: MarketFilter): MarketFilter => {
    if (f.tickers && f.tickers.length > 0) return f;
    let out = f;
    if (scopedClasses != null) {
      const ex = DEFAULT_EXCLUDED_CLASSES.filter((c) => !scopedClasses.has(c));
      const { excludeClasses: _drop, ...rest } = out;
      out = ex.length > 0 ? { ...rest, excludeClasses: ex } : rest;
    }
    if (scopedTypes != null) {
      const ex = DEFAULT_EXCLUDED_MARKET_TYPES.filter((t) => !scopedTypes.has(t));
      const { excludeMarketTypes: _drop, ...rest } = out;
      out = ex.length > 0 ? { ...rest, excludeMarketTypes: ex } : rest;
    }
    return out;
  };
  const muted = (): MarketFilter => ({ tickers: [NO_ROWS_TICKER], cadenceMs: filter.cadenceMs ?? BROWSER_CADENCE_MS });
  if (filter.tickers && filter.tickers.length > 0) {
    if (filter.tickers.length === 1 && filter.tickers[0] === NO_ROWS_TICKER) return filter;
    const kept = filter.tickers.filter((t) => { const f = tickerSportFamily(t); return f == null || families.has(f); });
    if (kept.length === 0) return muted();
    return kept.length === filter.tickers.length ? filter : { ...filter, tickers: kept };
  }
  let out = filter;
  if (filter.sports && filter.sports.length > 0) {
    const kept = filter.sports.filter((s) => keys.has(s.trim().toUpperCase()));
    if (kept.length === 0) return muted();
    if (kept.length !== filter.sports.length) out = { ...out, sports: kept };
  }
  if (axes.gamesAxis && needs?.gamesBroadFor && !broadHere(needs.gamesBroadFor)) {
    // No source is game-broad FOR this edge: its games axis is the named
    // keys of its families (a CFB board keeps he2 game-broad while an NFL
    // GameDetail narrows he5 to its event).
    const kept = [...(needs.namedGames ?? [])].filter((g) => inFamily(gameKeySportFamily(g))).sort();
    if (kept.length === 0) return muted();
    out = { ...out, games: kept };
  } else if (filter.games && filter.games.length > 0) {
    const kept = filter.games.filter((g) => inFamily(gameKeySportFamily(g)));
    if (kept.length === 0) return muted();
    if (kept.length !== filter.games.length) out = { ...out, games: kept };
  } else if (needs?.gamesBroadFor && broadHere(needs.gamesBroadFor) && out.games) {
    const { games: _drop, ...rest } = out;
    out = rest;
  }
  return withDenyLists(out);
}

/** D5.4 — the ALT-only mute. A feed whose live announce carries only ALT
 *  tokens (he6 `CFBALT`, he7 `NFLALT`) serves nothing a main page renders,
 *  and `sports:'all'` is encoded as the axis OMITTED (see `keysToNeeds`:
 *  `sport:*` → null → no `sports` on the wire), so a broad connection would
 *  pull every period line and prop onto every dashboard tab. This keeps the
 *  markets subscribe — the `activeSports` announce is answered ONLY to a
 *  markets subscribe (HYDRA_API "Active sports"), and the announce is what
 *  ownership and the FeedTape chip read — but sends the matches-nothing
 *  allow-list (`NO_ROWS_TICKER`) unless the aggregated needs name one of the
 *  feed's tokens.
 *
 *  Never an ALT key whole (wedge issue 1, 2026-10-02): a name unmutes ONLY
 *  the events named beside it (`altGames`) — of this edge's family — so a
 *  sibling broad for every sport (`sports:'all'`: the All-sports board,
 *  Live, an unaudited route), which drops the union's games axis, can no
 *  longer hand the ALT edge its whole sport.
 *  A name with no event (a source naming `CFBALT` without games) stays
 *  muted. A filter that already carries an exact `tickers` allow-list is
 *  left alone — the rows are named, the engine answers with those only —
 *  UNLESS this edge's events are named (g1xl-v2#1053, 2026-10-13): a
 *  GameDetail names its MAIN-edge rows by ticker (its edge's index) and its
 *  ALT rows by event, so the list — main-edge tickers, none of them here —
 *  must never AND the event's ALT rows away. The edge then gets the event
 *  bound with its own deny-lists, exactly what it gets beside a broad axis.
 *  `altTokens` null = not an ALT-only feed (or unknown yet) = no change. */
export function applyAltOnlyMute(
  filter: MarketFilter,
  needs: AggregatedNeeds,
  altTokens: ReadonlySet<string> | null,
): MarketFilter {
  if (altTokens == null || altTokens.size === 0) return filter;
  const cadenceMs = filter.cadenceMs ?? BROWSER_CADENCE_MS;
  const listed = filter.tickers != null && filter.tickers.length > 0;
  // `namedSports`, not `sports`: a broad sibling makes the union broad, but
  // the name is what unmutes.
  const named = [...(needs.namedSports ?? [])].map(normSport).filter((s) => altTokens.has(s));
  // Only this edge's events: another family's GameDetail must not re-filter
  // (and re-snapshot) this edge (per-edge scope).
  const families = new Set([...altTokens].map(sportFamily));
  const games = named.length === 0 ? [] : [...(needs.altGames ?? [])].filter((g) => familyServedBy(gameKeySportFamily(g), families)).sort();
  if (games.length === 0) return listed ? filter : { tickers: [NO_ROWS_TICKER], cadenceMs };
  const sports = [...new Set(named)].sort();
  if (listed) return { ...edgeDenyLists(altTokens, needs), sports, games, cadenceMs };
  return { ...filter, sports, games };
}

/** The deny-lists an engine serving `keys` gets from the opt-ins attributed
 *  to those keys (or to every sport) — `scopeMarketFilter`'s per-edge rule,
 *  for a filter built from nothing. Unattributed needs (hand-built fixtures)
 *  read the flat union. */
function edgeDenyLists(keys: ReadonlySet<string>, needs: AggregatedNeeds): Pick<MarketFilter, 'excludeClasses' | 'excludeMarketTypes'> {
  const optedIn = (bySport: ReadonlyMap<string, ReadonlySet<string>> | undefined, flat: ReadonlySet<string>): ReadonlySet<string> => {
    if (bySport == null) return flat;
    const out = new Set<string>();
    for (const [sport, values] of bySport) if (sport === '*' || keys.has(sport)) for (const v of values) out.add(v);
    return out;
  };
  const classes = optedIn(needs.classesBySport, needs.classes);
  const types = optedIn(needs.marketTypesBySport, needs.marketTypes);
  const out: Pick<MarketFilter, 'excludeClasses' | 'excludeMarketTypes'> = {};
  const excludeClasses = DEFAULT_EXCLUDED_CLASSES.filter((c) => !classes.has(c));
  if (excludeClasses.length > 0) out.excludeClasses = excludeClasses;
  const excludeMarketTypes = DEFAULT_EXCLUDED_MARKET_TYPES.filter((t) => !types.has(t));
  if (excludeMarketTypes.length > 0) out.excludeMarketTypes = excludeMarketTypes;
  return out;
}

/** g1xl-v2#1106 — every wire sport code g1 renders, uppercase and sorted:
 *  each registry spelling (the SPORT_TABS ids, golf, and the aliases such as
 *  NCAAF, ITF and the soccer competitions) and each one's ALT token. The core
 *  floor sends this list, less the cold-standby ALT keys, when the union is
 *  broad and an omission applies (Dan, 2026-10-08: hydracore's existing
 *  allow-list then applies the omission). A spelling the engine never uses
 *  costs nothing; a missing one would starve that sport on the floor. */
export const FLOOR_WIRE_SPORTS: readonly string[] = (() => {
  const codes = new Set<string>();
  for (const code of Object.keys(SPORT_NORMALIZE)) {
    const upper = code.trim().toUpperCase();
    codes.add(upper);
    codes.add(altWireTokenFor(upper));
  }
  return Object.freeze([...codes].sort());
})();

/** g1xl-v2#1106 — the ALT tokens the core floor leaves out: an ALT key on the
 *  floor is a COLD standby while an edge carries it.
 *
 *  `served` is the fleet's view of which ALT tokens an edge is serving (the
 *  `__filter_policy`; useHydra `negotiateFloorCadence`). A served token is
 *  omitted only when these needs hand it to the ALT edge, which is exactly
 *  when `applyAltOnlyMute` lifts that edge's mute for it: named beside an
 *  event of its family. Then the tab's floor admission already drops every
 *  floor frame of the token (`_feedRequests` sees the edge asking for it), so
 *  the floor copy is pure transfer: the whole ALT section at 1 s, read by
 *  nobody. A token named with no event (the dev prop workbench) leaves the
 *  ALT edge muted, the floor is its only source, and it stays. Computed from
 *  the same aggregated needs the edge's worker holds, so the floor and the
 *  edge switch at the same needs change, without waiting for an ack.
 *  Non-ALT tokens are never returned: MAIN sports stay on the floor as the
 *  warm standby (Dan, 2026-09-02). */
export function floorColdStandbySports(needs: AggregatedNeeds, served: Iterable<string> | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const raw of served ?? []) {
    const token = typeof raw === 'string' ? normSport(raw) : '';
    if (!token || !isAltWireToken(token)) continue;
    if (applyAltOnlyMute({}, needs, new Set([token])).sports?.includes(token)) out.add(token);
  }
  return out;
}

// ── Floor scope decay (floor-selection-churn, 2026-10-22) ──────────────────
// hc1 replays the WHOLE in-scope floor on every selection change — a
// narrowing included (every row re-sent to a tab that holds it; 13.8 MB for
// NFLALT alone, 27 MB for NFL+NFLALT, measured 2026-10-22). One gbuff
// consumer re-installed its floor filter 10–19 times per 10 minutes: each
// route change landed as three or four installs (the old page's sources
// leave after the 1 s flicker hold, the new page's arrive over the next few
// seconds, each a widening sent at once), and the ticker folds' 2 s
// re-derivation (a row lands → its sport and class are known → the union
// narrows) sent a pure-narrowing re-install a few seconds after every
// widening. The floor is the warm standby: a sport or class it carries for
// a little longer than needed costs 1 s-cadence live frames, dropped at
// ingest while an edge serves them; a replay costs tens of MB and seconds
// of head-of-line delay on everything else the floor carries. So the floor
// REMEMBERS: a sport, held sport or class opt-in stays on its filter for
// FLOOR_SCOPE_GRACE_MS after the last source needing it left, and a
// narrowing goes out only once nothing has needed it for that long. A
// widening still goes out promptly, but every floor filter change (a needs
// emit, a policy, an announce, a decay) is coalesced by the transport into
// ONE install: trailing FLOOR_SETTLE_MS from the latest change, bounded by
// FLOOR_SETTLE_MAX_MS from the first (Danielle R1 DNL-FSC-001 — a route
// change's sources land over several seconds, not one). The coalescing
// lives in the transports' markets path, never in the needs aggregator:
// the OddsPapi facets ride the same aggregator and keep their own timing
// (DNL-FSC-002). The connect-time first subscribe is never delayed. Floor
// only — an edge's scope is per-edge and must never widen to a sport
// nobody views.

/** How long the core floor keeps carrying a sport / held sport / class or
 *  market-type opt-in after the last source needing it left. The broad
 *  union (`sports` null) is remembered the same way, so a Live → board hop
 *  does not narrow the floor and then re-widen it on the way back. */
export const FLOOR_SCOPE_GRACE_MS = 30_000;
/** The floor install settle: a floor filter change is installed this long
 *  after the LATEST change (a later source arrival restarts it)… */
export const FLOOR_SETTLE_MS = 1_500;
/** …bounded by this from the FIRST change, so a source that keeps moving
 *  (a fold re-deriving every 2 s) cannot defer the install forever. */
export const FLOOR_SETTLE_MAX_MS = 5_000;

/** The floor's memory of what it recently carried: `widen` records the live
 *  union and returns it widened by everything still inside the grace.
 *  Bounded by the sport / class vocabulary. */
export class FloorScopeMemory {
  private readonly sports = new Map<string, number>();
  private readonly floorSports = new Map<string, number>();
  private readonly classes = new Map<string, number>();
  private readonly marketTypes = new Map<string, number>();
  /** Last time the union was sports-broad (null axis); -Infinity = never. */
  private broadAtMs = -Infinity;

  constructor(private readonly graceMs: number = FLOOR_SCOPE_GRACE_MS) {}

  /** Record `needs` as live at `now` and return the widened union. Keys
   *  within `slackMs` of expiry are dropped with the ones that expired, so
   *  a decay recompose does not chase expiries one second apart. */
  widen(needs: AggregatedNeeds, now: number, slackMs = 0): AggregatedNeeds {
    const note = (map: Map<string, number>, values: Iterable<string>): void => { for (const v of values) map.set(v, now); };
    if (needs.sports == null) this.broadAtMs = now; else note(this.sports, needs.sports);
    note(this.floorSports, needs.floorSports ?? []);
    note(this.classes, needs.classes);
    note(this.marketTypes, needs.marketTypes);
    const expired = (t: number): boolean => now - t + slackMs >= this.graceMs;
    const live = (map: Map<string, number>): Set<string> => {
      const out = new Set<string>();
      for (const [k, t] of map) { if (expired(t)) map.delete(k); else out.add(k); }
      return out;
    };
    const sports = live(this.sports);
    const floorSports = live(this.floorSports);
    const classes = live(this.classes);
    const marketTypes = live(this.marketTypes);
    const broad = needs.sports == null || !expired(this.broadAtMs);
    const union = (a: Set<string> | ReadonlySet<string> | null | undefined, b: Set<string>): Set<string> => new Set([...(a ?? []), ...b]);
    return {
      ...needs,
      sports: broad ? null : union(needs.sports, sports),
      floorSports: union(needs.floorSports, floorSports),
      classes: union(needs.classes, classes),
      marketTypes: union(needs.marketTypes, marketTypes),
    };
  }

  /** When the earliest remembered key leaves the grace (so the filter can be
   *  recomposed), or null when nothing is remembered. */
  nextExpiryMs(): number | null {
    let earliest = Infinity;
    for (const map of [this.sports, this.floorSports, this.classes, this.marketTypes]) for (const t of map.values()) earliest = Math.min(earliest, t);
    if (Number.isFinite(this.broadAtMs)) earliest = Math.min(earliest, this.broadAtMs);
    return Number.isFinite(earliest) ? earliest + this.graceMs : null;
  }
}

/** The proposed-axes advertisement on an announce (`activeSports.filterAxes`,
 *  g1xl-v2#1053 Part 3), or null when absent / malformed. Unknown entries
 *  are kept verbatim — the consumer only ever asks `has('lineScope')`. */
export function readFilterAxes(msg: { filterAxes?: unknown }): Set<string> | null {
  const raw = msg.filterAxes;
  if (!Array.isArray(raw)) return null;
  const out = new Set<string>();
  for (const a of raw) if (typeof a === 'string' && a.trim()) out.add(a.trim());
  return out;
}

/** Canonical string for change detection — two filters with the same axes in
 *  any order compare equal. */
export function filterKey(filter: MarketFilter): string {
  const sorted = (a?: string[]): string[] | undefined => (a ? [...a].sort() : undefined);
  return JSON.stringify({
    tickers: sorted(filter.tickers),
    sports: sorted(filter.sports),
    games: sorted(filter.games),
    excludeClasses: sorted(filter.excludeClasses),
    excludeMarketTypes: sorted(filter.excludeMarketTypes),
    lineScope: filter.lineScope,
    cadenceMs: filter.cadenceMs,
  });
}

// ── OddsPapi + game-state filters ───────────────────────────────────────────
// odds-gamestate-feed-filters, Stage C′ (spec: galactus-dev-workspace
// `docs/agents/handoffs/odds-gamestate-feed-filters/handoff.md` @ 2245b9f;
// wire truth: hydracore `Danielle-feature/hc10-op-gamestate` @ 5c0b8ad,
// HYDRA_API.md §"OddsPapi + game-state filters"). The markets lifecycle on
// two more channels — one versioned filter per (socket, channel), its own
// ack, generation and fence. The install streams the scoped snapshot itself
// (Begin → frames → End), so a filtered subscribe is never followed by an
// `{op:'snapshot'}`.

export type ChannelFilterChannel = 'oddspapi' | 'gamestate';

/** The `oddspapi` filter on the wire (spec D2). Scope = (sports ∧ games) ∨
 *  detailGames; a detailGames game is served whole, every other in-scope
 *  game projected to marketTypes × periods. An axis is never sent empty
 *  (the server rejects `[]`). */
export interface OpFilter {
  sports?: string[];
  games?: string[];
  marketTypes?: string[];
  periods?: string[];
  detailGames?: string[];
}

/** The `gamestate` filter on the wire (spec D2): sports ∧ games, records
 *  whole. g1 sends `{games: <interest keys>}`. */
export interface GameStateFilter {
  sports?: string[];
  games?: string[];
}

/** `filterAck` on `oddspapi` / `gamestate`. A rejection carries `error`
 *  and the CURRENT generation (0 for a rejected first filter, which
 *  subscribes nothing). `effective` echoes the present axes only. */
export interface HydraChannelFilterAckMsg {
  type: 'filterAck';
  channel: ChannelFilterChannel;
  socketIncarnation: string;
  filterGeneration: number;
  effective?: OpFilter;
  error?: string;
}

/** The games allow-list sent when nothing mounted needs a game on the
 *  channel (no source draws OddsPapi; no game-state reader has ever named a
 *  key). Matches no game key, so the channel is subscribed, acked and quiet
 *  (an empty scoped snapshot: rule 9′, nothing in scope) instead of the
 *  firehose an omitted axis would mean — the twin of `NO_ROWS_TICKER`. */
export const NO_GAMES = '__NO_GAMES__';

/** Build the `oddspapi` wire filter from the union's facets (spec D6):
 *  - any board facet open → V5's shape: the boards' sports (omitted when a
 *    board draws every sport), families and periods, plus `detailGames`
 *    when some source draws a game whole. A detail game's sport is never
 *    added to `sports` — `detailGames` stands on its own (D2).
 *  - detail facets only → V2's shape: `{games}`, those games whole.
 *  - nothing drawn → `{games:[NO_GAMES]}`.
 *  Never `{detailGames}` alone (it selects every game, V3) and never an
 *  empty axis. */
export function buildOpFilter(odds: AggregatedOdds | undefined): OpFilter {
  const o = odds ?? emptyOdds();
  const sorted = (s: Iterable<string>): string[] => [...s].sort();
  if (o.board && o.marketTypes.size > 0 && o.periods.size > 0 && (o.boardSports == null || o.boardSports.size > 0)) {
    const filter: OpFilter = {};
    if (o.boardSports != null) filter.sports = sorted(o.boardSports);
    filter.marketTypes = sorted(o.marketTypes);
    filter.periods = sorted(o.periods);
    if (o.detailGames.size > 0) filter.detailGames = sorted(o.detailGames);
    return filter;
  }
  if (o.detailGames.size > 0) return { games: sorted(o.detailGames) };
  return { games: [NO_GAMES] };
}

/** Canonical string for a channel filter — same axes in any order compare
 *  equal (change detection, the replace limiter). */
export function channelFilterKey(filter: OpFilter | GameStateFilter): string {
  const f = filter as OpFilter;
  const sorted = (a?: string[]): string[] | undefined => (a ? [...a].sort() : undefined);
  return JSON.stringify({
    sports: sorted(f.sports), games: sorted(f.games), marketTypes: sorted(f.marketTypes),
    periods: sorted(f.periods), detailGames: sorted(f.detailGames),
  });
}

/** Is this frame stamped with a filter pair? On a legacy socket a stamped
 *  frame is a protocol violation (D1a step 4); on a filtered one only the
 *  exact installed pair is admissible (`FilterFence.admits`). */
export function isStampedFrame(frame: object): boolean {
  const f = frame as { socketIncarnation?: unknown; filterGeneration?: unknown };
  return f.socketIncarnation !== undefined || f.filterGeneration !== undefined;
}

// ── Row predicate ───────────────────────────────────────────────────────────

/** The row fields the predicate reads. A market frame's `market` object, a
 *  store record, and a worker-cache entry all satisfy this structurally. */
export interface FilterableRow {
  ticker: string;
  sport?: string | null;
  /** The RAW wire sport key of a row the tab folded to its base sport
   *  (`'NFLALT'` beside `sport:'NFL'` — see `stampFeedSport`). The sports
   *  axis is evaluated on THIS when present: the engines matched the raw
   *  key, so a local re-evaluation must too, or a scope that dropped the
   *  ALT token would keep its rows resident. Absent on wire rows. */
  feedSport?: string | null;
  class?: string | null;
  marketType?: string | null;
  /** Either-key membership for the games axis; both optional on the wire. */
  canonicalGameKey?: string | null;
  canonicalGameKeyV2?: string | null;
}

/** Scope in any of its shapes (the wire request, the ack's `effective` echo,
 *  or a reset's `scope`) — arrays, as serialized. */
export type FilterScope = Pick<MarketFilter, 'tickers' | 'sports' | 'games' | 'excludeClasses' | 'excludeMarketTypes'>;

/** Row-vs-filter eligibility — the edge's `connWantsMarket` / core's
 *  `rowPassesFilter` semantics, verbatim: axes AND; allow-lists require a
 *  matching value; deny-lists are per-row fail-open (a row with no `class`
 *  passes every `excludeClasses`; no `marketType` passes every
 *  `excludeMarketTypes`). Case folding matches the server's (sports upper,
 *  class/type lower) so a locally-stamped row and a wire row agree. */
export function rowPassesFilter(scope: FilterScope, row: FilterableRow): boolean {
  if (scope.tickers && scope.tickers.length > 0 && !scope.tickers.includes(row.ticker)) return false;
  if (scope.sports && scope.sports.length > 0) {
    const raw = row.feedSport ?? row.sport;
    const s = raw ? normSport(raw) : null;
    if (!s || !scope.sports.some((x) => normSport(x) === s)) return false;
  }
  if (scope.games && scope.games.length > 0) {
    // Either-key membership, byte-for-byte with core's `rowInGames`: V2 is the
    // stricter doubleheader-safe key, and the legacy key keeps dhAmbiguous
    // rows (which carry NO V2 by contract) addressable. A row with neither key
    // is not this game's row — allow-lists require the field to be CARRIED.
    const legacy = row.canonicalGameKey ?? null;
    const v2 = row.canonicalGameKeyV2 ?? null;
    if (!legacy && !v2) return false;
    if (!scope.games.some((g) => g === legacy || g === v2)) return false;
  }
  if (scope.excludeClasses && scope.excludeClasses.length > 0 && row.class) {
    const c = normLower(row.class);
    if (scope.excludeClasses.some((x) => normLower(x) === c)) return false;
  }
  if (scope.excludeMarketTypes && scope.excludeMarketTypes.length > 0 && row.marketType) {
    const t = normLower(row.marketType);
    if (scope.excludeMarketTypes.some((x) => normLower(x) === t)) return false;
  }
  return true;
}

// ── Aggregator ──────────────────────────────────────────────────────────────

export interface FeedFilterAggregatorTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

/** The flicker window (g1xl-v2#1053, route transactions, 2026-10-02): a
 *  change that is not a pure widening of what is on the wire is HELD this
 *  long before it is emitted, so a mount/unmount pair or a quick
 *  back-and-forth inside one view sends nothing, and a route change lands
 *  as ONE emit straight to the new scope. Short on purpose — it sits
 *  beside Part 2b's one-replace-per-second bound; the 30 s key grace it
 *  replaces widened the wire to the whole sport for its whole length. */
export const FILTER_HOLD_MS = 1_000;
/** How long a `provisional` source (a board still fetching its index) may
 *  keep a transaction open before the live union is emitted regardless. */
export const PROVISIONAL_MAX_MS = 5_000;

export interface FeedFilterAggregatorOptions {
  /** Hold before a non-widening change is emitted (see FILTER_HOLD_MS).
   *  `0` = no hold: every change emits (debounced) — the tab registry,
   *  which publishes to the transports that own the real hold. */
  holdMs?: number;
  /** Upper bound on a `provisional` source's hold (see PROVISIONAL_MAX_MS). */
  provisionalMaxMs?: number;
  /** Coalesce window for change emission. A route change mounts several
   *  sources in one tick; one filter (one ack, one snapshot) should result.
   *  Defaults to 50 ms. `0` emits synchronously. */
  emitDebounceMs?: number;
  timers?: FeedFilterAggregatorTimers;
}

const setCovers = (inner: ReadonlySet<string> | null | undefined, outer: ReadonlySet<string> | null | undefined): boolean => {
  // null = every value (broad). Broad covers everything; a list covers only
  // a list it contains.
  if (outer == null) return true;
  if (inner == null) return false;
  for (const v of inner) if (!outer.has(v)) return false;
  return true;
};
const subsetOf = (a: ReadonlySet<string> | undefined, b: ReadonlySet<string> | undefined): boolean => {
  if (!a) return true;
  if (!b) return a.size === 0;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

/** Row-space order on aggregated needs: does `outer` admit every row (and
 *  deliver at least as fast as) `inner`? Axes AND, so each allow-axis of
 *  `outer` must be broad or contain `inner`'s; opt-ins (classes, market
 *  types) must contain `inner`'s; a `'main'` line scope is narrower than
 *  the whole ladder; the floor's sports ride like sports. A transition to
 *  a scope that covers the emitted one is a WIDENING and goes out at once;
 *  anything else (narrower, or sideways — a game page's event vs a board's
 *  allow-list) is held for the flicker window. Cadence: slower is held,
 *  faster goes out. */
export function needsCovers(inner: AggregatedNeeds, outer: AggregatedNeeds): boolean {
  if (!setCovers(inner.tickers, outer.tickers)) return false;
  if (!setCovers(inner.sports, outer.sports)) return false;
  if (!setCovers(inner.games, outer.games)) return false;
  if (!subsetOf(inner.classes, outer.classes)) return false;
  if (!subsetOf(inner.marketTypes, outer.marketTypes)) return false;
  if (!subsetOf(inner.floorSports, outer.floorSports)) return false;
  // More ALT events = more rows on the ALT edge: adding one is a widening.
  if (!subsetOf(inner.altGames ?? new Set(), outer.altGames ?? new Set())) return false;
  const outerLines = outer.lineScope ?? 'any';
  const innerLines = inner.lineScope ?? 'any';
  if (outerLines === 'main' && innerLines !== 'main') return false;
  if ((outer.cadenceMs ?? BROWSER_CADENCE_MS) > (inner.cadenceMs ?? BROWSER_CADENCE_MS)) return false;
  return oddsCovers(inner.odds, outer.odds);
}

/** OP order (D6): `outer` draws every game, family and period `inner` does.
 *  A detail game is covered only by a detail game (a board draws it
 *  projected, not whole). Opening a facet is a widening and goes out at
 *  once; closing one is held for the flicker window like any narrowing. */
function oddsCovers(inner: AggregatedOdds | undefined, outer: AggregatedOdds | undefined): boolean {
  const i = inner ?? emptyOdds();
  const o = outer ?? emptyOdds();
  if (!subsetOf(i.detailGames, o.detailGames)) return false;
  if (!i.board) return true;
  if (!o.board) return false;
  return setCovers(i.boardSports, o.boardSports) && subsetOf(i.marketTypes, o.marketTypes) && subsetOf(i.periods, o.periods);
}

/** Canonical string of a needs value — equality means "nothing to send". */
export function needsKey(needs: AggregatedNeeds): string {
  const list = (s: ReadonlySet<string> | null | undefined): string | null => (s == null ? null : [...s].sort().join(','));
  const bySport = (m: ReadonlyMap<string, ReadonlySet<string>> | undefined): string | null =>
    (m == null ? null : [...m.entries()].map(([k, v]) => `${k}=${[...v].sort().join(',')}`).sort().join(';'));
  return JSON.stringify({
    t: list(needs.tickers), s: list(needs.sports), g: list(needs.games), c: list(needs.classes), m: list(needs.marketTypes),
    n: list(needs.namedSports ?? new Set()), l: needs.lineScope ?? 'any', cd: needs.cadenceMs ?? null, f: list(needs.floorSports ?? new Set()),
    cs: bySport(needs.classesBySport), ms: bySport(needs.marketTypesBySport),
    tb: list(needs.tickersBroadFor ?? new Set()), gb: list(needs.gamesBroadFor ?? new Set()),
    nt: list(needs.namedTickers ?? new Set()), ng: list(needs.namedGames ?? new Set()),
    ag: list(needs.altGames ?? new Set()),
    op: oddsKey(needs.odds),
  });
}

function oddsKey(odds: AggregatedOdds | undefined): string {
  const o = odds ?? emptyOdds();
  const list = (s: ReadonlySet<string> | null): string | null => (s == null ? null : [...s].sort().join(','));
  // A closed board has no sports/families/periods worth comparing.
  return JSON.stringify(o.board
    ? { b: 1, s: list(o.boardSports), t: list(o.marketTypes), p: list(o.periods), d: list(o.detailGames) }
    : { b: 0, d: list(o.detailGames) });
}

/**
 * Per-source needs → live union → `onChange` with the new AggregatedNeeds.
 * Transport-agnostic: the worker feeds it per-port declarations, the
 * direct-WS client feeds it the tab's single declaration, the tab-side
 * registry feeds it per-view ones.
 *
 * ROUTE TRANSACTIONS (g1xl-v2#1053, 2026-10-02). The union is always the
 * LIVE union — a source that leaves takes its keys with it at once. What is
 * graced is the EMIT, never the keys: a change that widens what is on the
 * wire goes out immediately (a mounting view must get its rows); a change
 * that narrows or moves sideways is held for the flicker window and then
 * emitted as whatever the live union is at that moment. So a route change
 * (one view's keys replaced by another's inside the window) is ONE emit
 * straight to the new scope, and the wire never carries a scope wider than
 * both the old and the new one — the 30 s key grace this replaces kept a
 * left page's `sport:` keys alive beside the new page's and widened he2 to
 * the whole sport for 30 s on every navigation. A `provisional` source (a
 * board still fetching the index its allow-list comes from) extends the
 * hold up to `provisionalMaxMs`, so the transaction lands on the pick, not
 * on the muted step before it.
 */
export class FeedFilterAggregator<SourceId> {
  private perSource = new Map<SourceId, Set<string>>();
  private provisional = new Set<SourceId>();
  private lastEmitted: AggregatedNeeds | null = null;
  private lastEmittedKey: string | null = null;
  private emitHandle: unknown = null;
  private holdHandle: unknown = null;
  private holdSinceMs: number | null = null;
  private readonly holdMs: number;
  private readonly provisionalMaxMs: number;
  private readonly debounceMs: number;
  private readonly timers: FeedFilterAggregatorTimers;

  constructor(
    private readonly onChange: (needs: AggregatedNeeds) => void,
    opts: FeedFilterAggregatorOptions = {},
  ) {
    this.holdMs = opts.holdMs ?? FILTER_HOLD_MS;
    this.provisionalMaxMs = opts.provisionalMaxMs ?? PROVISIONAL_MAX_MS;
    this.debounceMs = opts.emitDebounceMs ?? 50;
    this.timers = opts.timers ?? {
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    };
  }

  /** Replace a source's declaration. `null` drops the source. */
  setNeeds(source: SourceId, needs: FeedNeeds | null): void {
    if (needs == null) {
      this.perSource.delete(source);
      this.provisional.delete(source);
    } else {
      this.perSource.set(source, needsToKeys(needs));
      if (needs.provisional === true) this.provisional.add(source);
      else this.provisional.delete(source);
    }
    this.reconcile();
  }

  removeSource(source: SourceId): void {
    this.perSource.delete(source);
    this.provisional.delete(source);
    this.reconcile();
  }

  hasSource(source: SourceId): boolean {
    return this.perSource.has(source);
  }

  /** Live union (what sources declare right now). */
  liveKeys(): Set<string> {
    const out = new Set<string>();
    for (const keys of this.perSource.values()) for (const k of keys) out.add(k);
    return out;
  }

  /** The live union as needs — what the next emit will carry. */
  current(): AggregatedNeeds {
    return { ...keysToNeeds(this.liveKeys()), provisional: this.provisional.size > 0 };
  }

  /** What was last EMITTED (what the wire is built from), or the live union
   *  before the first emit. Recompositions that do not come from a needs
   *  change (an announce, a cadence policy) build from THIS, so a held
   *  transaction never leaks early. */
  emitted(): AggregatedNeeds {
    return this.lastEmitted ?? this.current();
  }

  /** True while a change is held (the flicker window or a provisional source). */
  get holding(): boolean {
    return this.holdHandle != null;
  }

  private clearHold(): void {
    if (this.holdHandle != null) { this.timers.clearTimeout(this.holdHandle); this.holdHandle = null; }
    this.holdSinceMs = null;
  }

  /** Re-run the diff; called by every mutation. */
  reconcile(): void {
    const next = this.current();
    const key = needsKey(next);
    if (this.lastEmittedKey != null && key === this.lastEmittedKey) {
      // Back to what is on the wire (a flicker that settled): nothing to send.
      this.clearHold();
      if (this.emitHandle != null) { this.timers.clearTimeout(this.emitHandle); this.emitHandle = null; }
      return;
    }
    const provisional = this.provisional.size > 0;
    // The first declaration is a widening of nothing: it goes out at once.
    const widening = this.lastEmitted == null || needsCovers(this.lastEmitted, next);
    if (widening && !provisional) {
      this.clearHold();
      this.scheduleEmit();
      return;
    }
    if (this.holdMs <= 0 && !provisional) {
      this.scheduleEmit();
      return;
    }
    if (this.holdHandle == null) {
      this.holdSinceMs = Date.now();
      this.armHold(Math.max(this.holdMs, 0));
      return;
    }
    // A later change inside the window does not restart it: the newest union
    // wins at the window's end. A provisional source that just settled after
    // the window had already elapsed releases the transaction now.
    if (!provisional && this.holdSinceMs != null && Date.now() - this.holdSinceMs >= this.holdMs) {
      this.clearHold();
      this.scheduleEmit();
    }
  }

  private armHold(ms: number): void {
    this.holdHandle = this.timers.setTimeout(() => {
      this.holdHandle = null;
      const since = this.holdSinceMs ?? Date.now();
      if (this.provisional.size > 0 && Date.now() - since < this.provisionalMaxMs) {
        // Still provisional: wait out the rest of its bound, then emit anyway.
        this.armHold(Math.max(50, this.provisionalMaxMs - (Date.now() - since)));
        return;
      }
      this.holdSinceMs = null;
      this.scheduleEmit();
    }, ms);
  }

  /** Forget the hold and emit the live union now (a socket boundary: the
   *  new socket re-sends the complete filter anyway). */
  flushPending(): void {
    this.clearHold();
    if (this.emitHandle != null) { this.timers.clearTimeout(this.emitHandle); this.emitHandle = null; }
    this.emitNow();
  }

  private scheduleEmit(): void {
    if (this.debounceMs <= 0) { this.emitNow(); return; }
    if (this.emitHandle != null) return;
    this.emitHandle = this.timers.setTimeout(() => { this.emitHandle = null; this.emitNow(); }, this.debounceMs);
  }

  private emitNow(): void {
    const next = this.current();
    const key = needsKey(next);
    if (key === this.lastEmittedKey) return;
    this.lastEmitted = next;
    this.lastEmittedKey = key;
    this.onChange(next);
  }
}

// ── Fence ───────────────────────────────────────────────────────────────────

export type FenceAckResult<E = MarketFilterEffective> =
  | { kind: 'installed'; pair: FencePair; effective: E }
  | { kind: 'rejected'; error: string }
  | { kind: 'ignored'; reason: 'stale-incarnation' | 'not-newer' };

/** Any channel's `filterAck`, as the fence reads it. */
export interface FenceAck<E> {
  type?: string;
  channel?: string;
  socketIncarnation: unknown;
  filterGeneration: unknown;
  effective?: E;
  error?: string;
}

/**
 * A2′ client fencing for ONE connection. Exactly the four wire steps: the
 * server validates and accepts; emits `filterAck` before any frame of that
 * generation; the client installs the exact pair (and performs its reset);
 * only frames stamped with that exact pair are admissible.
 *
 * Socket boundaries call `reset()` — nothing is admitted on a new socket until
 * its own ack arrives, and a frame from a previous socket can never match
 * (incarnations are globally unique by construction).
 *
 * One fence per (socket, channel): the `oddspapi` and `gamestate` filters
 * carry their own generations (odds-gamestate-feed-filters D1), so each
 * channel fences on its own pair; `E` is that channel's `effective` echo.
 */
export class FilterFence<E = MarketFilterEffective> {
  private installed: FencePair | null = null;
  /** The incarnation of the socket we currently hold an ack from — a
   *  late ack from an older socket (any other incarnation, once one is
   *  installed) is ignored. */
  private incarnation: string | null = null;

  get pair(): FencePair | null { return this.installed; }

  /** Socket boundary (open / close / reconnect): forget everything. */
  reset(): void {
    this.installed = null;
    this.incarnation = null;
  }

  onAck(ack: FenceAck<E>): FenceAckResult<E> {
    if (typeof ack.socketIncarnation !== 'string' || typeof ack.filterGeneration !== 'number') {
      return { kind: 'rejected', error: 'malformed_ack' };
    }
    if (this.incarnation != null && ack.socketIncarnation !== this.incarnation) {
      return { kind: 'ignored', reason: 'stale-incarnation' };
    }
    if (ack.error || !ack.effective) {
      // Rejection: the server retained the prior filter + generation, so the
      // installed pair (if any) stays authoritative. A rejected FIRST filter
      // installs nothing — and the server sends nothing, so nothing is
      // admitted either.
      return { kind: 'rejected', error: ack.error ?? 'invalid_filter' };
    }
    if (this.installed && this.installed.socketIncarnation === ack.socketIncarnation
      && ack.filterGeneration <= this.installed.filterGeneration) {
      return { kind: 'ignored', reason: 'not-newer' };
    }
    this.incarnation = ack.socketIncarnation;
    this.installed = { socketIncarnation: ack.socketIncarnation, filterGeneration: ack.filterGeneration };
    return { kind: 'installed', pair: { ...this.installed }, effective: ack.effective };
  }

  /** Exact-pair admission. Unstamped frames are NOT admissible on a fenced
   *  connection (a filtered connection stamps every markets frame; an
   *  unstamped one can only be a legacy-path frame that does not belong). */
  admits(frame: { socketIncarnation?: unknown; filterGeneration?: unknown }): boolean {
    const p = this.installed;
    if (!p) return false;
    return frame.socketIncarnation === p.socketIncarnation && frame.filterGeneration === p.filterGeneration;
  }
}
